// Member hover, sidebar → map: hovering a member row (or the relation's header row)
// draws that member on the map. Crosslink relations get the map hover's own drawing
// (all ranges, no cursor), plus an orange halo round the hovered member (lane or
// road); CaJ along_* members only their SPOT point; any other member the whole
// feature or its role's range. The bar/profile position hover draws
// on a layer of its own, so the member rows' clearing never wipes it.
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { addCrosslinkHoverLayer } from "../layers/CrosslinkHoverLayer";
import { addMemberHoverLayer } from "../layers/MemberHoverLayer";
import { drawCrosslinkRelation } from "../layers/featureinfohandlers/crosslinkDrawing";
import {
  pairCrosslinkRanges,
  rangesOvershoot,
} from "../layers/featureinfohandlers/crosslinkPairing.mjs";
import { getRoleRangesCm, sliceOlLineByCm } from "../util";
import { baseKey } from "./lrValues.mjs";
import { findMemberFeature, memberHighlightPlan } from "./memberLookup.mjs";

let state = null;

/** Main viewer only; without it members are looked up in the row's own source. */
export function installMemberHover(map) {
  const memberLayer = addMemberHoverLayer(map);
  const crosslinkLayer = addCrosslinkHoverLayer(map);
  const positionLayer = addMemberHoverLayer(map);
  state = {
    map,
    own: [memberLayer, crosslinkLayer, positionLayer],
    memberSource: memberLayer.getSource(),
    crosslinkSource: crosslinkLayer.getSource(),
    positionSource: positionLayer.getSource(),
  };
}

function otherSources() {
  if (!state) return [];
  return state.map
    .getAllLayers()
    .filter(
      (layer) =>
        !state.own.includes(layer) &&
        typeof layer.getSource()?.getFeatureById === "function",
    )
    .map((layer) => ({
      source: layer.getSource(),
      visible: layer.getVisible(),
    }));
}

/** The member's feature: the relation's own source, then other visible layers. */
export function lookupMember(id, ownSource) {
  return findMemberFeature(id, ownSource ?? null, otherSources());
}

const isCrosslink = (model) =>
  model.groups.some((g) =>
    g.tags.some((t) => baseKey(t.key) === "type" && t.value === "crosslink"),
  );

function highlight(feature, plan, { halo = false } = {}) {
  const geometry = feature.getGeometry();
  const line = geometry.getType() === "LineString";
  let shapes = [geometry.clone()];
  if (line && plan.kind === "range")
    shapes = plan.ranges.map((r) =>
      sliceOlLineByCm(geometry, r.startCm, r.endCm ?? Infinity),
    );
  if (line && plan.kind === "spot")
    shapes = [new Point(geometry.getCoordinateAt(plan.fraction))];
  shapes.forEach((shape) =>
    state.memberSource.addFeature(
      new Feature({ geometry: shape, nonInteractive: true, halo }),
    ),
  );
}

function drawCrosslink(entry) {
  const [a, b] = entry.model.members;
  const featureA = lookupMember(a.id, entry.source);
  const featureB = lookupMember(b.id, entry.source);
  if (!featureA || !featureB) return false;
  const rangesA = getRoleRangesCm(a.role);
  const rangesB = getRoleRangesCm(b.role);
  const paired = pairCrosslinkRanges(rangesA, rangesB, -1);
  drawCrosslinkRelation(state.crosslinkSource, {
    relation: entry.feature,
    featureA,
    featureB,
    rangesA: paired.hovered,
    rangesB: paired.other,
    outOfBounds:
      rangesOvershoot(rangesA, featureA.get("lengthCm")) ||
      rangesOvershoot(rangesB, featureB.get("lengthCm")),
  });
  return true;
}

/**
 * @param {{model: object, feature: Feature, source: object}} entry a registry entry
 * @param {number|null} memberIndex the hovered member row, or null for the header row
 */
export function showMemberHover(entry, memberIndex) {
  if (!state) return;
  clearMemberHover();
  const { members } = entry.model;
  const crosslink =
    isCrosslink(entry.model) && members.length >= 2 && drawCrosslink(entry);
  if (crosslink && memberIndex === null) return;
  members
    .filter((m) => memberIndex === null || m.index === memberIndex)
    .forEach((m) => {
      const feature = lookupMember(m.id, entry.source);
      if (!feature) return;
      const length = feature.get("lengthCm");
      highlight(
        feature,
        memberHighlightPlan(m, Number.isFinite(length) ? length : null),
        { halo: crosslink },
      );
    });
}

export function clearMemberHover() {
  if (!state) return;
  state.memberSource.clear();
  state.crosslinkSource.clear();
}

/**
 * Bar/profile hover, sidebar → map: the range under the pointer and the point at `cm`.
 * @param {Feature} feature the entry's feature (a LineString, or nothing is drawn)
 * @param {number|null} cm
 * @param {{startCm: number, endCm: number}|null} range
 */
export function showPositionHover(feature, cm, range) {
  if (!state) return;
  state.positionSource.clear();
  const geometry = feature?.getGeometry();
  if (geometry?.getType() !== "LineString") return;
  const add = (shape) =>
    state.positionSource.addFeature(
      new Feature({ geometry: shape, nonInteractive: true }),
    );
  if (range) add(sliceOlLineByCm(geometry, range.startCm, range.endCm));
  if (cm !== null)
    add(new Point(sliceOlLineByCm(geometry, cm, cm).getFirstCoordinate()));
}

export function clearPositionHover() {
  state?.positionSource.clear();
}
